import React, { useState, useEffect } from 'react';
// 1. IMPORTANTE: Agregamos useLocation para recibir los datos de la Landing
import { useLocation, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, Package, Clock, AlertCircle, ArrowRightLeft, User, Phone, Scale, Ruler, CheckCircle2, FileText, Lock as LockIcon } from 'lucide-react';
import api from '../../api/axios';
import { echo } from '../../utils/echo';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Componente para mover el mapa
function RecenterMap({ coords }) {

  const map = useMap();
  useEffect(() => {
    if (coords && coords[0] && coords[1]) {
      map.flyTo(coords, 15);
    }
  }, [coords, map]);
  return null;
}
const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

export default function TrackingPage() {
  const [liveLocation, setLiveLocation] = useState(null);
  const [searchParams] = useSearchParams(); // <--- Ponla aquí
  const guiaDesdeUrl = searchParams.get('guia'); // <--- Y esta aquí
  const location = useLocation(); // 2. Inicializamos el lector de la ubicación
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState(null);

  const [isDark, setIsDark] = useState(
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // 3. LA MAGIA: Este efecto corre al cargar la página
  useEffect(() => {
    // 1. Buscamos la guía ya sea que venga del Perfil (guiaDesdeUrl) o de la Landing (location.state)
    const guiaRecibida = guiaDesdeUrl || location.state?.initialTracking;

    if (guiaRecibida) {
      setTrackingNumber(guiaRecibida); // Llena el cuadrito de texto
      fetchData(guiaRecibida);         // Llama a la API de Más Envíos
    }
  }, [guiaDesdeUrl, location.state]);
  // EFECTO DE TIEMPO REAL: Se activa cuando 'shipment' tiene datos
  // EFECTO DE TIEMPO REAL: Se activa cuando 'shipment' tiene datos
  useEffect(() => {
    if (shipment?.id) {
      // 1. Nos conectamos al canal PÚBLICO (echo.channel en lugar de echo.private)
      const channel = echo.channel(`shipment.${shipment.id}`);

      // 2. Escuchamos el evento de Laravel. 
      // OJO: El punto '.' al inicio de '.location-update' es obligatorio aquí.
      channel.listen('.location-update', (data) => {
        console.log("¡📍 Ubicación en vivo recibida en la WEB!", data);
        setLiveLocation({
          lat: Number(data.latitude),
          lng: Number(data.longitude)
        });
      });

      // 3. Limpiamos la conexión si el usuario cierra la página
      return () => {
        channel.stopListening('.location-update');
        echo.leaveChannel(`shipment.${shipment.id}`);
      };
    }
  }, [shipment]);
  // 4. FUNCIÓN REUTILIZABLE DE BÚSQUEDA
  const fetchData = async (numero) => {
    if (!numero) return;
    setLoading(true);
    setError(null);
    setShipment(null);

    try {
      // ✅ AHORA USAMOS 'api' (tu archivo axios.js)
      const response = await api.get(`/shipments/${numero}`);

      // Axios ya trae los datos en response.data, no necesitas hacer response.json()
      setShipment(response.data);
    } catch (err) {
      // Axios captura errores de red y de estado (404, 500) automáticamente
      if (err.response?.status === 404) {
        setError('La guía ingresada no existe.');
      } else {
        setError('Error al consultar el servidor.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    fetchData(trackingNumber);
  };

  const metallicGradient = { background: 'linear-gradient(to bottom, #F1F1F1, #C1C1C1)' };
  // Determinamos qué coordenadas usar
  // 1. Ajuste de la lógica de coordenadas
  const currentCoords =
    liveLocation // Prioridad 1: Tiempo real (WebSockets)
      ? [Number(liveLocation.lat), Number(liveLocation.lng)]
      : (shipment?.current_lat && shipment?.current_lng) // Prioridad 2: Última posición guardada en BD
        ? [Number(shipment.current_lat), Number(shipment.current_lng)]
        : (shipment?.originBranch?.lat && shipment?.originBranch?.lng) // Prioridad 3: Sucursal origen
          ? [Number(shipment.originBranch.lat), Number(shipment.originBranch.lng)]
          : [19.4326, -99.1332]; // Fallback final (CDMX)

  // 2. Ajuste del handlePrint para evitar problemas con la variable de entorno
  const handlePrint = () => {
    // Usamos el baseURL que ya definimos en tu instancia de axios 
    // o el import.meta.env si es necesario
    const baseUrl = import.meta.env.VITE_BACKEND_URL;
    const url = `${baseUrl}/api/shipments/${shipment.tracking_number}/label`;
    window.open(url, '_blank');
  };
  return (
    <div className="relative min-h-screen pt-6 pb-12">
      {/* CAPAS DE FONDO (Recuerda que si las pusiste en el Layout, puedes quitarlas de aquí) */}
      <img src="/fondo.jpg" alt="Fondo" className="fixed inset-0 w-full h-full object-cover z-0 opacity-90" />
      <div className="fixed inset-0 bg-black/70 z-0 pointer-events-none"></div>
      <div className="fixed inset-0 z-0 opacity-60 pointer-events-none" style={{ background: 'radial-gradient(circle at center 120%, #CE010B 0%, transparent 70%)' }}></div>

      <div className="relative z-10 max-w-7xl mx-auto p-6 space-y-6">

        {/* SECCIÓN A: Buscador - MÓVIL (Cuadrado Centrado) */}
        <div className={`block lg:hidden w-full transition-all duration-500 ${shipment ? 'py-4 overflow-x-auto snap-x snap-mandatory flex flex-row gap-6 px-4 justify-start items-stretch scrollbar-none' : 'flex flex-col items-center justify-start pt-8 h-[calc(100vh-160px)] min-h-[420px]'}`}>

          <div className={`flex flex-col gap-6 shrink-0 snap-align-center ${shipment ? 'w-full max-w-[80vw]' : 'w-full max-w-[80vw] mx-auto'}`}>
            {/* Tarjeta de Buscador */}
            <section
              className="w-full min-h-[500px] flex flex-col justify-between items-center p-10 rounded-[3rem] shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
              style={isDark ? {} : metallicGradient}
            >
              <h2
                className="text-5xl font-black text-center text-black dark:text-white uppercase tracking-tight leading-none my-4"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Rastreo de Guías
              </h2>

              <form onSubmit={handleManualSearch} className="flex flex-col justify-between flex-grow w-full gap-8 mt-6">
                <div className="relative w-full">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-white" size={32} />
                  <input
                    type="text"
                    placeholder="TBT-########"
                    className="w-full pl-20 pr-4 py-6 border rounded-2xl focus:outline-none uppercase font-black text-2xl transition-all bg-white/80 border-black/10 focus:border-[#CE010B] dark:bg-white/10 dark:border-white dark:text-white dark:placeholder:text-white/40 dark:focus:ring-2 dark:focus:ring-white"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !trackingNumber}
                  className="w-full py-6 rounded-2xl font-black transition-all shadow-md active:scale-95 disabled:opacity-70 text-2xl flex items-center justify-center gap-2 bg-[#CE010B] text-white hover:bg-red-800 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[2px] dark:border-white dark:text-white"
                >
                  {loading ? 'Buscando...' : (
                    <>
                      <span>Rastrear</span>
                      <Search size={26} />
                    </>
                  )}
                </button>
              </form>

              {/* ERROR en Móvil */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border w-full justify-center bg-red-100 text-red-700 border-red-200 dark:bg-white/10 dark:text-white dark:border-white animate-fade-in mt-2">
                  <AlertCircle size={14} className="shrink-0" />
                  <p className="text-[10px] font-bold">{error}</p>
                </div>
              )}
            </section>

            {/* SECCIÓN B MÓVIL: Origen y Destino */}
            {shipment && (
              <div
                className="w-full flex-grow flex flex-col justify-center px-8 py-8 rounded-[2.5rem] shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
                style={isDark ? {} : metallicGradient}
              >
                <div className="flex flex-col items-center justify-between gap-6 h-full">

                  {/* Remitente */}
                  <div className="flex-1 text-center">
                    <div className="inline-block px-10 py-3 rounded-full font-black shadow-sm mb-4 uppercase tracking-widest text-lg transition-colors bg-[#CE010B] text-white dark:bg-white dark:text-black"
                    >
                      {shipment.route_details?.origin_branch?.city || 'Origen'}
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 mb-1 justify-center">
                      <User className="size-6.5 text-gray-600 dark:text-white" />
                      <span className="text-lg font-black uppercase tracking-widest">Remitente</span>
                    </div>

                    <p className="text-3xl font-black text-black dark:text-white uppercase leading-tight tracking-tight">
                      {shipment.sender_name}
                    </p>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 justify-center mt-2">
                      <Phone className="size-6.5" />
                      <span className="text-lg font-black">{shipment.sender_phone}</span>
                    </div>
                  </div>

                  {/* Ícono de Ruta */}
                  <div className="transform rotate-90 transition-colors text-black/30 dark:text-white my-6"
                  >
                    <ArrowRightLeft className="size-12" strokeWidth={2.5} />
                  </div>

                  {/* Destinatario */}
                  <div className="flex-1 text-center">
                    <div className="inline-block px-10 py-3 rounded-full font-black shadow-sm mb-4 uppercase tracking-widest text-lg transition-colors bg-[#CE010B] text-white dark:bg-white dark:text-black"
                    >
                      {shipment.route_details?.destination_branch?.city || 'Destino'}
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 mb-1 justify-center">
                      <span className="text-lg font-black uppercase tracking-widest">Destinatario</span>
                      <User className="size-6.5 text-gray-600 dark:text-white" />
                    </div>

                    <p className="text-3xl font-black text-black dark:text-white uppercase leading-tight tracking-tight">
                      {shipment.recipient_name}
                    </p>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 justify-center mt-2">
                      <span className="text-lg font-black">{shipment.recipient_phone}</span>
                      <Phone className="size-6.5" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* COLUMNA 2 MÓVIL: Guía Lista + Detalles del Envío */}
          {shipment && (
            <div className="flex flex-col gap-6 shrink-0 snap-align-center w-[370px] max-w-[92vw]">
              {/* SECCIÓN B.2 MÓVIL: Detalles de los Paquetes */}
              <div
                className="w-full h-[370px] max-w-[92vw] max-h-[92vw] aspect-square flex flex-col p-8 rounded-[2.5rem] shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
                style={isDark ? {} : metallicGradient}
              >
                {/* Título de la sección */}
                <h3
                  className="text-2xl font-black mb-6 flex items-center gap-2 border-b pb-4 transition-colors border-black/10 text-black dark:border-white/20 dark:text-white"
                  style={{ fontFamily: "'Secular One', sans-serif" }}
                >
                  <Package className="text-[#CE010B] dark:text-white size-7.5" />
                  Contenido del Envío
                </h3>

                <div className="grid grid-cols-1 gap-4 overflow-y-auto pr-1 flex-grow scrollbar-none">
                  {shipment.packages && shipment.packages.length > 0 ? (
                    shipment.packages.map((pkg, index) => (
                      <div
                        key={pkg.id}
                        className="relative overflow-hidden group transition-all p-5 flex flex-col gap-3 rounded-xl border bg-white/60 border-black/5 dark:bg-black/20 dark:border-white/10"
                      >
                        {/* Número de fondo (Índice) */}
                        <div className="absolute -right-2 -top-2 font-black text-7xl transition-colors text-black/5 dark:text-white/5"
                        >
                          {index + 1}
                        </div>

                        {/* Descripción del paquete */}
                        <p className="font-bold text-xl uppercase truncate pr-8 transition-colors text-black dark:text-white"
                        >
                          {pkg.description || 'Sin descripción'}
                        </p>

                        <div className="flex items-center justify-between mt-1">
                          {/* Peso */}
                          <div className="flex items-center gap-2 text-gray-700 dark:text-white/80">
                            <Scale className="text-[#CE010B] dark:text-white size-5.5" />
                            <span className="text-base font-black">{pkg.weight} kg</span>
                          </div>

                          {/* Dimensiones */}
                          {(pkg.length > 0) && (
                            <div className="flex items-center gap-2 text-gray-700 dark:text-white/80">
                              <Ruler className="text-gray-400 dark:text-white/60 size-5.5" />
                              <span className="text-sm font-bold">
                                {pkg.length}x{pkg.width}x{pkg.height} cm
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm italic text-center w-full text-gray-500 dark:text-white/50">
                      Cargando detalles de los bultos...
                    </p>
                  )}
                </div>
              </div>

              {/* Tarjeta de Guía Lista */}
              <section
                className="w-full flex-grow min-h-[420px] max-w-[92vw] flex flex-col justify-between items-center p-8 rounded-[2.5rem] shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white text-center gap-5"
                style={isDark ? {} : metallicGradient}
              >
                {/* ÍCONO DINÁMICO */}
                <div className={`p-3 rounded-full border border-transparent dark:border-white mb-1 ${shipment.payment?.status === 'approved' ? 'bg-green-100 text-green-600 dark:bg-white/10 dark:text-white' : 'bg-orange-100 text-orange-600 dark:bg-white/10 dark:text-orange-400'}`}>
                  {shipment.payment?.status === 'approved' ? <CheckCircle2 className="size-8" /> : <Clock className="size-8" />}
                </div>

                {/* TÍTULO DINÁMICO */}
                <h3 className="text-3xl font-black text-black dark:text-white mb-1" style={{ fontFamily: "'Secular One', sans-serif" }}>
                  {shipment.payment?.status === 'approved' ? '¡Guía Lista!' : 'Pago Pendiente'}
                </h3>

                <div className="p-4 rounded-xl w-full bg-white/60 dark:bg-black/20 border border-dashed border-gray-300 dark:border-white mb-2">
                  <p className="text-xs text-gray-500 dark:text-white/70 uppercase font-black tracking-widest mb-0.5">Rastreo</p>
                  <p className="text-2xl font-black text-[#CE010B] dark:text-white">{shipment.tracking_number}</p>
                </div>

                {/* BOTÓN INTELIGENTE */}
                {shipment.payment?.status === 'approved' ? (
                  <button
                    onClick={handlePrint}
                    className="w-full flex items-center justify-center gap-2 font-bold py-5 px-4 rounded-xl bg-gray-900 text-white dark:bg-white dark:text-black hover:scale-105 transition-transform uppercase cursor-pointer border-none text-lg"
                  >
                    <FileText className="size-5" /> Descargar PDF
                  </button>
                ) : (
                  <div className="w-full flex items-center justify-center gap-2 font-bold py-5 px-4 rounded-xl bg-gray-100 text-gray-500 dark:bg-black/40 dark:text-white/50 cursor-not-allowed border border-dashed border-gray-300 dark:border-white/20 uppercase text-base">
                    EN ESPERA DE PAGO... <LockIcon className="size-4.5 ml-1" />
                  </div>
                )}

                <div className="w-full h-[1px] bg-gray-200 dark:bg-white/10 my-2"></div>

                <div className="w-full text-left space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-white/60">Subtotal:</span> <span className="font-bold text-black dark:text-white">${shipment.total_price}</span></div>
                  <div className="flex justify-between text-lg font-black border-t pt-2.5 border-gray-200 dark:border-white/20"><span className="text-black dark:text-white">TOTAL:</span> <span className="text-[#CE010B] dark:text-white">${shipment.total_price} MXN</span></div>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* SECCIÓN A: Buscador - DESKTOP (Original) */}
        <section
          className="hidden lg:block max-w-2xl mx-auto px-8 py-6 rounded-2xl shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
          style={isDark ? {} : metallicGradient}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <h2
              className="text-xl font-regular text-black dark:text-white uppercase tracking-tight whitespace-nowrap"
              style={{ fontFamily: "'Secular One', sans-serif" }}
            >
              Rastreo de Guías
            </h2>

            <form onSubmit={handleManualSearch} className="flex flex-col md:flex-row gap-3 items-center w-full md:w-auto flex-grow max-w-xl">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-white" size={18} />

                <input
                  type="text"
                  placeholder="TBT-########"
                  className="w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none uppercase font-medium text-sm transition-all bg-white/80 border-black/10 focus:border-[#CE010B] dark:bg-white/10 dark:border-white dark:text-white dark:placeholder:text-white/40 dark:focus:ring-2 dark:focus:ring-white"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !trackingNumber}
                className="px-8 py-3 rounded-xl font-bold transition-all shadow-md active:scale-95 disabled:opacity-70 flex-shrink-0 text-sm flex items-center justify-center gap-2 bg-[#CE010B] text-white hover:bg-red-800 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[2px] dark:border-white dark:text-white"
              >
                {loading ? 'Buscando...' : (
                  <>
                    <span>Rastrear</span>
                    <Search size={16} className="hidden dark:block" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* ERROR: Adaptado al modo oscuro */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg mt-4 border max-w-md mx-auto transition-colors bg-red-100 text-red-700 border-red-200 dark:bg-white/10 dark:text-white dark:border-white">
              <AlertCircle size={16} />
              <p className="text-xs font-bold">{error}</p>
            </div>
          )}
        </section>

        {shipment && (

          <div className="space-y-6 animate-fade-in">
            {/* 📦 CONTENEDOR PRINCIPAL: Dividimos la pantalla en 2 columnas en PC (lg:flex-row) y apilamos en móviles (flex-col) */}
            <div className="flex flex-col items-start lg:grid lg:grid-cols-3 lg:items-stretch gap-6 w-full">

              {/* ⬅️ COLUMNA IZQUIERDA: Tarjeta PDF */}
              <div className="hidden lg:block lg:col-span-1 lg:h-full">
                {/* 🟢 SECCIÓN DE PDF ACTUALIZADA (Validación por tabla Payments) 🟢 */}
                <div
                  className="w-full max-w-2xl lg:max-w-sm mx-auto px-8 lg:px-6 py-8 lg:py-4 rounded-2xl shadow-xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[2px] dark:border-white flex flex-col items-center justify-between text-center gap-4 lg:gap-3 lg:h-full"
                  style={isDark ? {} : metallicGradient}
                >
                  {/* ÍCONO DINÁMICO: Verde si está APROBADO, Naranja si no */}
                  <div className={`p-3 lg:p-2 rounded-full border border-transparent dark:border-white ${shipment.payment?.status === 'approved' ? 'bg-green-100 text-green-600 dark:bg-white/10 dark:text-white' : 'bg-orange-100 text-orange-600 dark:bg-white/10 dark:text-orange-400'}`}>
                    {shipment.payment?.status === 'approved' ? <CheckCircle2 className="size-8 lg:size-6" /> : <Clock className="size-8 lg:size-6" />}
                  </div>

                  {/* TÍTULO DINÁMICO */}
                  <h3 className="text-2xl lg:text-lg font-bold text-black dark:text-white" style={{ fontFamily: "'Secular One', sans-serif" }}>
                    {shipment.payment?.status === 'approved' ? '¡Guía Lista!' : 'Pago Pendiente'}
                  </h3>

                  <div className="p-5 lg:p-3 rounded-xl w-full bg-white/60 dark:bg-black/20 border border-dashed border-gray-300 dark:border-white">
                    <p className="text-xs lg:text-[9px] text-gray-500 dark:text-white/70 uppercase font-black tracking-widest mb-0.5">Rastreo</p>
                    <p className="text-2xl lg:text-xl font-black text-[#CE010B] dark:text-white">{shipment.tracking_number}</p>
                  </div>

                  {/* BOTÓN INTELIGENTE: Verifica estrictamente si es 'approved' */}
                  {shipment.payment?.status === 'approved' ? (
                    <button
                      onClick={handlePrint}
                      className="w-full flex items-center justify-center gap-2 font-bold py-4 lg:py-2.5 px-4 rounded-xl bg-gray-900 text-white dark:bg-white dark:text-black hover:scale-105 transition-transform uppercase cursor-pointer border-none text-base lg:text-sm"
                    >
                      <FileText className="size-5 lg:size-4" /> Descargar PDF
                    </button>
                  ) : (
                    <div className="w-full flex items-center justify-center gap-2 font-bold py-4 lg:py-2.5 px-4 rounded-xl bg-gray-100 text-gray-500 dark:bg-black/40 dark:text-white/50 cursor-not-allowed border border-dashed border-gray-300 dark:border-white/20 uppercase text-sm lg:text-xs">
                      EN ESPERA DE PAGO... <LockIcon className="size-4.5 lg:size-3.5 ml-1" />
                    </div>
                  )}

                  <div className="w-full h-[1px] bg-gray-200 dark:bg-white/10 my-2 lg:my-1"></div>

                  <div className="w-full text-left space-y-2 lg:space-y-1">
                    <div className="flex justify-between text-sm lg:text-xs"><span className="text-gray-500 dark:text-white/60">Subtotal:</span> <span className="font-bold text-black dark:text-white">${shipment.total_price}</span></div>
                    <div className="flex justify-between text-xl lg:text-base font-black border-t pt-2 lg:pt-1 border-gray-200 dark:border-white/20"><span className="text-black dark:text-white">TOTAL:</span> <span className="text-[#CE010B] dark:text-white">${shipment.total_price} MXN</span></div>
                  </div>
                </div>
              </div>

              {/* ➡️ COLUMNA DERECHA: Origen/Destino y Paquetes (se apilan uno sobre otro gracias a flex-col) */}
              <div className="hidden lg:flex lg:col-span-2 flex-col gap-6">

                {/* SECCIÓN B: Origen y Destino */}
                <div
                  className="max-w-2xl lg:max-w-none mx-auto px-8 py-8 lg:py-6 rounded-2xl shadow-2xl transition-all duration-500 w-full border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
                  style={isDark ? {} : metallicGradient}
                >
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">

                    {/* Remitente */}
                    <div className="flex-1 text-center md:text-left">
                      <div className="inline-block px-8 lg:px-6 py-2.5 lg:py-1 rounded-full font-bold shadow-sm mb-4 lg:mb-3 uppercase tracking-widest text-xs lg:text-[10px] transition-colors bg-[#CE010B] text-white dark:bg-white dark:text-black"
                      >
                        {shipment.route_details?.origin_branch?.city || 'Origen'}
                      </div>

                      <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 mb-1 lg:mb-0.5 justify-center md:justify-start">
                        {/* Ícono de Usuario Dinámico */}
                        <User className="size-4.5 lg:size-3 text-gray-600 dark:text-white" />
                        <span className="text-xs lg:text-[9px] font-bold uppercase tracking-widest">Remitente</span>
                      </div>

                      <p className="text-xl lg:text-base font-black text-black dark:text-white uppercase leading-tight">
                        {shipment.sender_name}
                      </p>

                      <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 justify-center md:justify-start mt-2 lg:mt-1">
                        <Phone className="size-4.5 lg:size-3" />
                        <span className="text-sm lg:text-xs font-semibold">{shipment.sender_phone}</span>
                      </div>
                    </div>

                    {/* Ícono de Ruta: Blanco y más visible en Dark Mode */}
                    <div className="transform rotate-90 md:rotate-0 transition-colors text-black/30 dark:text-white my-6 md:my-0"
                    >
                      <ArrowRightLeft className="size-10 lg:size-8" strokeWidth={2} />
                    </div>

                    {/* Destinatario */}
                    <div className="flex-1 text-center md:text-right">
                      <div className="inline-block px-8 lg:px-6 py-2.5 lg:py-1 rounded-full font-bold shadow-sm mb-4 lg:mb-3 uppercase tracking-widest text-xs lg:text-[10px] transition-colors bg-[#CE010B] text-white dark:bg-white dark:text-black"
                      >
                        {shipment.route_details?.destination_branch?.city || 'Destino'}
                      </div>

                      <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 mb-1 lg:mb-0.5 justify-center md:justify-end">
                        <span className="text-xs lg:text-[9px] font-bold uppercase tracking-widest">Destinatario</span>
                        <User className="size-4.5 lg:size-3 text-gray-600 dark:text-white" />
                      </div>

                      <p className="text-xl lg:text-base font-black text-black dark:text-white uppercase leading-tight">
                        {shipment.recipient_name}
                      </p>

                      <div className="flex items-center gap-2 text-gray-600 dark:text-white/70 justify-center md:justify-end mt-2 lg:mt-1">
                        <span className="text-sm lg:text-xs font-semibold">{shipment.recipient_phone}</span>
                        <Phone className="size-4.5 lg:size-3" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECCIÓN B.2: Detalles de los Paquetes */}
                <div
                  className="max-w-2xl lg:max-w-none mx-auto px-8 py-8 lg:py-6 rounded-2xl shadow-2xl transition-all duration-500 mt-6 w-full border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
                  style={isDark ? {} : metallicGradient}
                >
                  {/* Título de la sección */}
                  <h3
                    className="text-xl lg:text-lg font-bold mb-6 flex items-center gap-2 border-b pb-4 lg:pb-3 transition-colors border-black/10 text-black dark:border-white/20 dark:text-white"
                    style={{ fontFamily: "'Secular One', sans-serif" }}
                  >
                    <Package className="text-[#CE010B] dark:text-white size-6 lg:size-[22px]" />
                    Contenido del Envío
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shipment.packages && shipment.packages.length > 0 ? (
                      shipment.packages.map((pkg, index) => (
                        <div
                          key={pkg.id}
                          className="relative overflow-hidden group transition-all p-5 lg:p-4 flex flex-col gap-3 lg:gap-2 rounded-xl border bg-white/60 border-black/5 dark:bg-black/20 dark:border-white/10"
                        >
                          {/* Número de fondo (Índice) */}
                          <div className="absolute -right-2 -top-2 font-black text-6xl lg:text-5xl transition-colors text-black/5 dark:text-white/5"
                          >
                            {index + 1}
                          </div>

                          {/* Descripción del paquete */}
                          <p className="font-bold text-base lg:text-sm uppercase truncate pr-8 transition-colors text-black dark:text-white"
                          >
                            {pkg.description || 'Sin descripción'}
                          </p>

                          <div className="flex items-center justify-between mt-1">
                            {/* Peso */}
                            <div className="flex items-center gap-2 text-gray-700 dark:text-white/80">
                              <Scale className="text-[#CE010B] dark:text-white size-4.5 lg:size-3.5" />
                              <span className="text-sm lg:text-xs font-bold">{pkg.weight} kg</span>
                            </div>

                            {/* Dimensiones */}
                            {(pkg.length > 0) && (
                              <div className="flex items-center gap-2 text-gray-700 dark:text-white/80">
                                {/* Ícono de regla: Gris en claro, Blanco en oscuro */}
                                <Ruler className="text-gray-400 dark:text-white/60 size-4.5 lg:size-3.5" />
                                <span className="text-xs lg:text-[10px] font-medium">
                                  {pkg.length}x{pkg.width}x{pkg.height} cm
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm italic text-center w-full text-gray-500 dark:text-white/50">
                        Cargando detalles de los bultos...
                      </p>
                    )}
                  </div>
                </div>

              </div>
            </div>
            {/* 📦 FIN DEL CONTENEDOR PRINCIPAL */}
            {/* SECCIÓN C: Historial y Mapa */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* COLUMNA 1: HISTORIAL */}
              <div
                className="order-2 lg:order-1 lg:col-span-1 w-[calc(100%-32px)] lg:w-full max-w-[764px] lg:max-w-none ml-4 lg:ml-0 px-6 py-6 lg:p-6 rounded-2xl shadow-2xl transition-all duration-500 border-[1.5px] border-black/10 dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-[3px] dark:border-white"
                style={isDark ? {} : metallicGradient}
              >
                {/* Título del Historial */}
                <h3
                  className="text-2xl lg:text-lg font-black mb-4 lg:mb-6 flex items-center justify-center lg:justify-start gap-2 border-b pb-3 lg:pb-3 transition-colors border-black/10 text-black dark:border-white/20 dark:text-white"
                  style={{ fontFamily: "'Secular One', sans-serif" }}
                >
                  {/* Icono de Reloj Dinámico */}
                  <Clock className="text-[#CE010B] dark:text-white size-8 lg:size-5" />
                  Historial
                </h3>

                {/* Línea de Tiempo */}
                <div className="space-y-5 lg:space-y-5 relative before:absolute before:inset-y-0 before:left-1/2 lg:before:left-0 before:-translate-x-1/2 lg:before:translate-x-0 lg:before:ml-[1.15rem] before:w-[2px] transition-colors before:bg-gray-400 dark:before:bg-white/30"
                >
                  {[...shipment.timeline].reverse().map((event, index) => (
                    <div key={event.id} className="relative flex flex-col lg:flex-row gap-2 lg:gap-4 items-center lg:items-start group">

                      {/* Círculos / Iconos de la línea de tiempo */}
                      <div className={`z-10 w-12 h-12 lg:w-10 lg:h-10 rounded-full flex items-center justify-center shrink-0 border-[2px] transition-all
                    ${index === 0
                          ? 'bg-[#CE010B] text-white border-[#CE010B] dark:bg-white dark:text-[#590000] dark:border-white scale-110 shadow-lg'
                          : 'bg-white text-gray-400 border-gray-200 dark:bg-black/40 dark:text-white/50 dark:border-white/20'}`}
                      >
                        {index === 0 ? (
                          /* Icono de paquete para el estado actual */
                          <Package className="w-6 h-6 lg:w-4.5 lg:h-4.5" />
                        ) : (
                          /* Punto para estados anteriores */
                          <div className="w-4 h-4 lg:w-2.5 lg:h-2.5 rounded-full bg-current" />
                        )}
                      </div>

                      {/* Información del Evento */}
                      <div className="w-full max-w-[280px] lg:max-w-none flex flex-col items-center lg:items-start text-center lg:text-left bg-white/70 dark:bg-black/40 p-4 lg:p-0 rounded-2xl border border-black/5 dark:border-white/10 lg:border-none shadow-md lg:shadow-none z-10">
                        <p className={`font-black text-2xl lg:text-sm transition-colors
                      ${index === 0
                            ? 'text-black dark:text-white'
                            : 'text-gray-700 dark:text-white/60'}`}
                        >
                          {event.status}
                        </p>
                        <p className="text-sm lg:text-[11px] font-semibold text-gray-500 dark:text-white/40 mt-1 lg:mt-0.5">
                          {formatDateTime(event.created_at)}
                        </p>

                        {/* --- AQUÍ ESTÁ LA NUEVA LÓGICA DINÁMICA --- */}
                        <p className="text-sm lg:text-[11px] font-black text-gray-600 dark:text-white/50 mt-1 lg:mt-0.5">
                          {event.branch?.name
                            ? event.branch.name
                            : event.status?.toLowerCase() === 'entregado'
                              ? 'Destino Final'
                              : event.status?.toLowerCase() === 'arribado'
                                ? 'Punto de control logístico'
                                : 'En tránsito'
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* COLUMNA 2: MAPA EN VIVO */}
              <div className="order-1 lg:order-2 lg:col-span-2 w-[calc(100%-32px)] lg:w-full max-w-[764px] lg:max-w-none ml-4 lg:ml-0 h-[550px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl relative transition-all duration-500 border-[1.5px] border-black dark:border-[3px] dark:border-white">
                <MapContainer
                  key={shipment.id} // <-- Importante: reinicia el mapa al cambiar de envío
                  center={currentCoords}
                  zoom={14}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <Marker position={currentCoords}>
                    <Popup>
                      <p className="font-bold text-[#CE010B] m-0">
                        {liveLocation ? 'Unidad en Movimiento' : 'Unidad TBT'}
                      </p>
                    </Popup>
                  </Marker>

                  {/* Componente que mueve la cámara automáticamente */}
                  <RecenterMap coords={currentCoords} />
                </MapContainer>

                {/* Overlay de Estatus sobre el mapa */}
                <div className="absolute bottom-4 left-4 z-[1000] bg-white/90 dark:bg-[#590000] dark:border dark:border-white/50 px-5 lg:px-4 py-3 lg:py-2 rounded-lg shadow-lg">
                  <div className="flex items-center gap-2">
                    {/* El circulito parpadea si la señal es en vivo */}
                    <div className={`w-2.5 h-2.5 lg:w-2 lg:h-2 rounded-full ${liveLocation ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                    <p className="text-[11px] lg:text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-white/70">
                      {liveLocation ? 'Rastreo en Tiempo Real' : 'Ubicación Actual'}
                    </p>
                  </div>
                  <p className="text-base lg:text-sm font-black text-black dark:text-white mt-1 lg:mt-0">
                    {liveLocation ? 'Repartidor en ruta' : 'En ruta a destino'}
                  </p>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}